import type { ViewAsMode } from "@starsach/shared-types";

/**
 * ADR-0011: Gairm staff open a customer's portal in its own tab at
 * `/as/<organisation id>/…`. The prefix is the router's basename, so the
 * whole app runs unchanged underneath it, and it is what tells the HTTP
 * client to send the view-as headers. The server does the rest: it scopes
 * and refuses exactly as it would for the customer, and records any
 * change under the staff member's own name.
 *
 * The mode (read by default; write once the staff member asks) is kept
 * per tab and per organisation in sessionStorage, and changing it reloads
 * the tab so every query is re-asked with the new header.
 */
export interface ViewAsLocation {
  organisationId: string;
  basename: string;
}

const PREFIX = /^\/as\/([\w.-]{1,128})(?=\/|$)/;

export function viewAsFromLocation(pathname: string = window.location.pathname): ViewAsLocation | undefined {
  const m = PREFIX.exec(pathname);
  return m ? { organisationId: m[1]!, basename: `/as/${m[1]!}` } : undefined;
}

const modeKey = (organisationId: string) => `starsach-view-as-mode:${organisationId}`;

export function viewAsMode(organisationId: string): ViewAsMode {
  return window.sessionStorage.getItem(modeKey(organisationId)) === "write" ? "write" : "read";
}

export function setViewAsMode(organisationId: string, mode: ViewAsMode): void {
  if (mode === "read") window.sessionStorage.removeItem(modeKey(organisationId));
  else window.sessionStorage.setItem(modeKey(organisationId), mode);
}

/** The headers every request carries inside a view-as tab; none outside one. */
export function viewAsHeaders(): Record<string, string> {
  const v = viewAsFromLocation();
  if (!v) return {};
  return { "x-starsach-view-as": v.organisationId, "x-starsach-view-as-mode": viewAsMode(v.organisationId) };
}

/** Where "View their portal" opens: the customer's own page, inside their view. */
export function viewAsUrl(organisationId: string, path: string): string {
  return `/as/${encodeURIComponent(organisationId)}${path.startsWith("/") ? path : `/${path}`}`;
}
